import type { DashboardId, FormName } from '@clinspace/shared';
import { Dashboard, type ViewRegion } from '../Dashboard.ts';
import type { CanvasPanel } from '../CanvasPanel.ts';
import { PALETTE, hbars, heatmap, list, type Rect } from '../charts.ts';
import { formDetail } from '../details.ts';
import { hoveredRow } from './Enrollment.ts';

/** Missing Data: missing-field %, forms not started, overdue by site; visits x forms heatmap. */
export class MissingDataDashboard extends Dashboard {
  readonly id: DashboardId = 'MissingData';

  protected override segmentLabel(segment: string): string {
    if (segment.startsWith('cell:')) return segment.slice(5).replace('|', ' · ');
    if (segment.startsWith('form:')) {
      const f = this.view.form(segment.slice(5));
      return f ? `${f.subjectId}` : segment;
    }
    return segment;
  }

  protected drawContent(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    const [cell, form] = this.path;
    if (cell && form) {
      const f = this.view.form(form.slice(5));
      if (f) {
        this.detail = formDetail(this.view, f);
        this.drawDetail(ctx, panel, area, this.detail);
      }
      return;
    }
    this.detail = undefined;
    const forms = this.view.forms(this.filters);
    const missingOf = (f: (typeof forms)[number]) =>
      f.status === 'Not Started' ? f.totalFields : f.missingFields.length;
    if (cell) {
      const [visitCode, formName] = cell.slice(5).split('|');
      const items = forms.filter(
        (f) => f.visitCode === visitCode && f.form === formName && missingOf(f) > 0,
      );
      const rows = list(
        ctx,
        ['Subject', 'Site', 'Status', 'Missing', 'Due'],
        items.map((f) => ({
          cells: [
            f.subjectId,
            this.view.siteLabel(f.siteId),
            f.status,
            f.status === 'Not Started' ? `all ${f.totalFields} fields` : f.missingFields.join(', '),
            `${f.dueDate}${f.overdue ? ' (overdue)' : ''}`,
          ],
          flag: f.overdue ? 'x' : undefined,
          colour: PALETTE.crimson,
        })),
        area,
        {
          title: `${formName} at ${visitCode} · ${items.length} subjects with missing data`,
          widths: [0.14, 0.22, 0.14, 0.32, 0.18],
          hoveredIndex: hoveredRow(panel, 'row:'),
        },
      );
      rows.forEach((r, i) =>
        panel.region<ViewRegion>({
          id: `row:${i}`,
          ...r,
          data: { kind: 'drill', segment: `form:${items[i]!.id}` },
          tooltip: [`${items[i]!.subjectId} · ${items[i]!.form}`, 'Open the page'],
        }),
      );
      return;
    }
    const expected = forms.reduce((s, f) => s + f.totalFields, 0);
    const missing = forms.reduce((s, f) => s + missingOf(f), 0);
    this.kpis(
      panel,
      ctx,
      [
        {
          label: 'Missing fields',
          value: expected ? `${Math.round((missing / expected) * 1000) / 10}%` : '—',
          colour: PALETTE.crimson,
          sub: `${missing} of ${expected} expected`,
        },
        {
          label: 'Forms not started',
          value: String(forms.filter((f) => f.status === 'Not Started').length),
        },
        {
          label: 'Overdue forms',
          value: String(forms.filter((f) => f.overdue).length),
          colour: PALETTE.amber,
        },
        { label: 'CRF pages', value: String(forms.length) },
      ],
      { x: area.x, y: area.y, w: area.w, h: 76 },
    );
    const visits = this.view.file.study.visitSchedule.map((v) => v.code);
    const formNames = this.view.file.study.forms.map((f) => f.name);
    const grid = new Map<string, { miss: number; exp: number; count: number }>();
    for (const f of forms) {
      const key = `${f.visitCode}|${f.form}`;
      const g = grid.get(key) ?? { miss: 0, exp: 0, count: 0 };
      g.miss += missingOf(f);
      g.exp += f.totalFields;
      g.count += missingOf(f) > 0 ? 1 : 0;
      grid.set(key, g);
    }
    const left: Rect = { x: area.x, y: area.y + 86, w: area.w * 0.66, h: area.h - 86 };
    const hovered = panel.hoveredRegion?.id.startsWith('cell:')
      ? (panel.hoveredRegion.id.slice(5).split(':').map(Number) as [number, number])
      : undefined;
    const cells = heatmap(
      ctx,
      formNames,
      visits,
      (r, c) => {
        const g = grid.get(`${visits[c]}|${formNames[r]}`);
        return g && g.exp ? Math.round((g.miss / g.exp) * 1000) / 10 : undefined;
      },
      left,
      { max: 25, hovered, labelWidth: 118, format: (v) => `${v}%` },
    );
    cells.forEach((row, r) =>
      row.forEach((rect, c) => {
        const g = grid.get(`${visits[c]}|${formNames[r]}`);
        if (!g) return;
        panel.region<ViewRegion>({
          id: `cell:${r}:${c}`,
          ...rect,
          data: { kind: 'drill', segment: `cell:${visits[c]}|${formNames[r] as FormName}` },
          tooltip: [
            `${formNames[r]} at ${visits[c]}`,
            `${g.miss} of ${g.exp} fields missing · ${g.count} subjects`,
            'Open the subjects',
          ],
        });
      }),
    );
    const right: Rect = {
      x: area.x + area.w * 0.69,
      y: area.y + 86,
      w: area.w * 0.31,
      h: area.h - 86,
    };
    const sites = this.view.file.sites
      .map((site) => {
        const fs = forms.filter((f) => f.siteId === site.id);
        const exp = fs.reduce((s, f) => s + f.totalFields, 0);
        const miss = fs.reduce((s, f) => s + missingOf(f), 0);
        return {
          siteId: site.id,
          label: site.city,
          value: exp ? Math.round((miss / exp) * 1000) / 10 : 0,
          overdue: fs.filter((f) => f.overdue).length,
        };
      })
      .sort((a, b) => b.value - a.value);
    // On the wide centre screen (2026-10-07) the overdue forms per site stand under the
    // worst sites, in the space the list leaves.
    const split = this.wide > 1 && right.h >= 2 * (20 + sites.length * 24) + 12;
    const top: Rect = split ? { ...right, h: 20 + sites.length * 26 + 4 } : right;
    const rows = hbars(
      ctx,
      sites.map((s) => ({
        label: s.label,
        value: s.value,
        sub: `${s.value}%`,
        colour: s.value > 5 ? PALETTE.crimson : PALETTE.teal,
      })),
      top,
      {
        title: 'Worst sites (missing %)',
        max: Math.max(10, ...sites.map((s) => s.value)),
        labelWidth: 96,
        hoveredIndex: hoveredRow(panel, 'site:'),
      },
    );
    rows.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `site:${i}`,
        ...r,
        data: { kind: 'filter', filterKey: 'siteId' },
        tooltip: [
          this.view.siteLabel(sites[i]!.siteId),
          `${sites[i]!.value}% missing · ${sites[i]!.overdue} overdue forms`,
          'Filter by site',
        ],
      }),
    );
    if (!split) return;
    const overdue = sites.slice().sort((a, b) => b.overdue - a.overdue);
    const late = hbars(
      ctx,
      overdue.map((s) => ({ label: s.label, value: s.overdue, colour: PALETTE.amber })),
      { x: right.x, y: top.y + top.h + 12, w: right.w, h: right.h - top.h - 12 },
      { title: 'Overdue forms per site', labelWidth: 96, hoveredIndex: hoveredRow(panel, 'late:') },
    );
    late.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `late:${i}`,
        ...r,
        data: { kind: 'info' },
        tooltip: [this.view.siteLabel(overdue[i]!.siteId), `${overdue[i]!.overdue} overdue forms`],
        selectable: false,
      }),
    );
  }
}
